import type { DocumentProvenance } from "@esi/shared";
import { Bot } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Link, projectConversationPath } from "../router.tsx";

/**
 * "Generated by agent" (openspec change `artefacts-to-knowledge`): content the assistant wrote and
 * someone added to knowledge. A quiet icon-and-text mark like the audience line, never a badge or
 * a state colour: it is provenance, not a warning.
 */
export function GeneratedMark({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      <Bot aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
      {t("generated.mark")}
    </span>
  );
}

/**
 * Where a generated document came from, as the API resolved it for this viewer: "From Pump
 * maintenance · version 3 · Claude Sonnet". The conversation is named (and linked) only for a
 * viewer who can open it; others read "From a conversation", everyone "From a deleted
 * conversation" once it is gone.
 */
export function ProvenanceLine({
  provenance,
  className,
}: {
  provenance: DocumentProvenance;
  className?: string;
}) {
  const { t } = useTranslation();
  const c = provenance.conversation;
  const from = provenance.deletedConversation ? (
    t("generated.fromDeleted")
  ) : c?.title ? (
    <>
      {t("generated.fromNamedPrefix")}
      <Link
        href={
          c.projectId
            ? projectConversationPath(c.projectId, c.id)
            : `/c/${encodeURIComponent(c.id)}`
        }
        className="text-accent-text underline-offset-4 hover:underline"
      >
        {c.title}
      </Link>
    </>
  ) : (
    t("generated.fromConversation")
  );
  return (
    <span
      className={cn(
        "flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-fg-muted tabular-nums",
        className,
      )}
    >
      <GeneratedMark />
      <span aria-hidden="true">·</span>
      <span className="min-w-0 break-words">{from}</span>
      <span aria-hidden="true">·</span>
      <span>{t("generated.version", { version: provenance.artefactVersion })}</span>
      {provenance.model ? (
        <>
          <span aria-hidden="true">·</span>
          <span>{provenance.model}</span>
        </>
      ) : null}
    </span>
  );
}
